/**
 * ESS POS — Stage 2: Cashier Workspace
 *
 * Implements:
 * 1. Cashier Dashboard (Today's sales, transaction count, pending bills, active shift, payment breakdown)
 * 2. New Sale POS Screen (Search, category filters, availability guard, live cart, notes, discounts)
 * 3. Payment Workflow (Cash with live change calculation, M-Pesa with backend verification guard, idempotent checkout)
 * 4. Pending Table Bills (Handoff from Waiter workspace)
 * 5. Completed Sales, Thermal Receipt Preview & Print, Authorized Reversal Request
 * 6. Shift Opening, Closing & Cash Drawer Reconciliation
 */

import { apiClient } from '../api/apiClient';
import { restaurantMockAdapter } from '../api/mockAdapter';
import { openFastSwitchStaffModal, openLockStationModal } from './waiterWorkspace';
import {
  escapeHtml,
  exportRowsToCsv,
  formatCurrency,
  formatDateTime,
  formatTimeShort,
  openModal,
  renderEmptyState,
  renderErrorState,
  renderLoadingState,
  showToast,
} from '../components/ui';
import {
  BranchRecord,
  CashierShiftRecord,
  CategoryRecord,
  ProductRecord,
  ProductVariantRecord,
  RestaurantOrder,
  SaleRecord,
  StockLocationRecord,
  QrOrderSubmissionRecord,
  ActiveBillRecord,
} from '../types/contracts';

interface CartLineItem {
  variantId: string;
  productId: string;
  productName: string;
  variantName: string;
  sku: string;
  unitPriceCents: number;
  maxAvailableStock: number;
  quantity: number;
  discountCents: number;
  notes: string;
}

const cartState: {
  items: CartLineItem[];
  linkedTableOrderId: string | null;
  linkedTableName: string | null;
  searchQuery: string;
  selectedCategoryId: string;
} = {
  items: [],
  linkedTableOrderId: null,
  linkedTableName: null,
  searchQuery: '',
  selectedCategoryId: 'ALL',
};

export async function renderCashierWorkspace(
  viewport: HTMLElement,
  activeTab: string,
  onNavigateTab: (tabId: string) => void
) {
  viewport.innerHTML = renderLoadingState('Loading Cashier POS & Shift Context...');

  try {
    const [branches, locations, categories, products, activeShift, sales, incomingQrOrders, activeQrBills] = await Promise.all([
      apiClient.listBranches(),
      apiClient.listStockLocations(),
      apiClient.listCategories(),
      apiClient.listProducts({ forceRefresh: true }),
      apiClient.getActiveShift(),
      apiClient.listSales(undefined, 50),
      apiClient.listIncomingQrOrders('PENDING').catch(() => []),
      apiClient.listActiveBills().catch(() => []),
    ]);

    const tenantId = apiClient.getEffectiveTenantId();
    const waiterOrders = restaurantMockAdapter.listOrders(tenantId);
    const pendingTableBills = waiterOrders.filter(
      (o) => o.status === 'AWAITING_PAYMENT' || o.status === 'SERVED'
    );

    const primaryBranch: BranchRecord | undefined = branches[0];
    const retailLocation: StockLocationRecord | undefined =
      locations.find((l) => l.type === 'RETAIL') || locations[0];

    // Clear background QR polling timer when on any tab other than incoming-qr-orders
    if (activeTab !== 'incoming-qr-orders' && qrPollingTimer) {
      clearInterval(qrPollingTimer);
      qrPollingTimer = null;
    }

    if (activeTab === 'incoming-qr-orders') {
      renderIncomingQrOrdersScreen(viewport, {
        incomingOrders: incomingQrOrders,
        onRefresh: () => renderCashierWorkspace(viewport, activeTab, onNavigateTab),
        onNavigateTab,
      });
    } else if (activeTab === 'dashboard') {
      renderCashierDashboard(viewport, {
        sales,
        activeShift,
        pendingTableBills,
        incomingQrCount: incomingQrOrders.length,
        onNavigateTab,
      });
    } else if (activeTab === 'pending-bills') {
      renderPendingBillsScreen(viewport, {
        pendingTableBills,
        activeQrBills,
        products,
        onNavigateTab,
      });
    } else if (activeTab === 'completed-sales') {
      renderCompletedSalesScreen(viewport, {
        sales,
        tenantId,
        onRefresh: () => renderCashierWorkspace(viewport, activeTab, onNavigateTab),
      });
    } else if (activeTab === 'shift-drawer') {
      renderShiftDrawerScreen(viewport, {
        activeShift,
        primaryBranch,
        sales,
        onRefresh: () => renderCashierWorkspace(viewport, activeTab, onNavigateTab),
      });
    } else if (activeTab === 'profile') {
      renderCashierProfileScreen(viewport, {
        sales,
        activeShift,
        primaryBranch,
        onRefresh: () => renderCashierWorkspace(viewport, activeTab, onNavigateTab),
        onNavigateTab,
      });
    } else {
      // Default: 'pos-sale'
      renderPosBillingScreen(viewport, {
        categories,
        products,
        activeShift,
        primaryBranch,
        retailLocation,
        pendingBillsCount: pendingTableBills.length + activeQrBills.length,
        incomingQrCount: incomingQrOrders.length,
        onNavigateTab,
        onSaleCompleted: () => renderCashierWorkspace(viewport, 'pos-sale', onNavigateTab),
      });
    }
  } catch (err: any) {
    viewport.innerHTML = renderErrorState({
      title: 'Failed to Load Cashier Workspace',
      message: err?.message || 'Unable to fetch POS catalog or shift state.',
      retryButtonId: 'cashier-retry-btn',
    });
    viewport.querySelector('#cashier-retry-btn')?.addEventListener('click', () => {
      renderCashierWorkspace(viewport, activeTab, onNavigateTab);
    });
  }
}

// ============================================================================
// 1. CASHIER DASHBOARD
// ============================================================================

function renderCashierDashboard(
  viewport: HTMLElement,
  params: {
    sales: SaleRecord[];
    activeShift: CashierShiftRecord | null;
    pendingTableBills: RestaurantOrder[];
    incomingQrCount?: number;
    onNavigateTab: (tab: string) => void;
  }
) {
  const completedSales = params.sales.filter((s) => s.status === 'COMPLETED');
  const totalRevenueCents = completedSales.reduce((sum, s) => sum + s.total_cents, 0);
  const cashRevenueCents = completedSales
    .filter((s) => s.payment_method === 'CASH')
    .reduce((sum, s) => sum + s.total_cents, 0);
  const mpesaRevenueCents = completedSales
    .filter((s) => s.payment_method === 'MPESA')
    .reduce((sum, s) => sum + s.total_cents, 0);
  const otherRevenueCents = totalRevenueCents - cashRevenueCents - mpesaRevenueCents;

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <!-- Top Action Bar -->
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Cashier Shift &amp; Daily Summary</h2>
          <p class="ess-section-desc">Real-time billing metrics, drawer status, and pending waiter bills</p>
        </div>
        <div class="ess-btn-group">
          ${
            (params.incomingQrCount ?? 0) > 0
              ? `<button type="button" class="ess-btn ess-btn-primary" id="dash-quick-qr" style="background:#f59e0b;border-color:#d97706;">
                  🛎️ Incoming QR Orders (${params.incomingQrCount})
                </button>`
              : ''
          }
          <button type="button" class="ess-btn ess-btn-secondary" id="dash-go-shift">
            ${params.activeShift ? 'Shift Open • View Drawer' : 'Open Cashier Shift'}
          </button>
          <button type="button" class="ess-btn ess-btn-primary" id="dash-go-pos">
            + Launch New Sale (POS)
          </button>
        </div>
      </div>

      ${
        (params.incomingQrCount ?? 0) > 0
          ? `
        <div class="ess-alert ess-alert-warning" style="display:flex;justify-content:space-between;align-items:center;background:#fffbeb;border:1px solid #f59e0b;padding:12px 16px;border-radius:6px;">
          <div style="display:flex;align-items:center;gap:12px;">
            <span style="font-size:22px;">🛎️</span>
            <div>
              <strong style="color:#b45309;font-size:14px;">${params.incomingQrCount} Incoming Table QR Order(s) Awaiting Confirmation</strong>
              <div style="font-size:12px;color:#78350f;">Orders submitted by diners from table QR codes must be accepted before active bills and kitchen KOT are created.</div>
            </div>
          </div>
          <button type="button" class="ess-btn ess-btn-primary" id="dash-alert-incoming-qr" style="background:#d97706;border-color:#b45309;">
            Review Queue (${params.incomingQrCount})
          </button>
        </div>
      `
          : ''
      }

      <!-- KPI Metric Cards -->
      <div class="ess-kpi-grid">
        <div class="ess-card ess-kpi-card ess-kpi-highlight">
          <div class="ess-kpi-label">Today's Completed Sales</div>
          <div class="ess-kpi-value">${formatCurrency(totalRevenueCents)}</div>
          <div class="ess-kpi-meta">${completedSales.length} verified transactions</div>
        </div>

        <div class="ess-card ess-kpi-card" style="${(params.incomingQrCount ?? 0) > 0 ? 'border: 2px solid #f59e0b;' : ''}">
          <div class="ess-kpi-label">Incoming QR Orders</div>
          <div class="ess-kpi-value" style="${(params.incomingQrCount ?? 0) > 0 ? 'color:#d97706;' : ''}">${params.incomingQrCount ?? 0}</div>
          <div class="ess-kpi-meta">${(params.incomingQrCount ?? 0) > 0 ? 'Awaiting Cashier Review' : 'Queue Clear'}</div>
        </div>

        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Active Table Bills</div>
          <div class="ess-kpi-value">${params.pendingTableBills.length}</div>
          <div class="ess-kpi-meta">
            ${formatCurrency(params.pendingTableBills.reduce((s, b) => s + b.totalCents, 0))} awaiting settlement
          </div>
        </div>

        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Current Shift Drawer</div>
          <div class="ess-kpi-value">
            ${
              params.activeShift
                ? formatCurrency(
                    params.activeShift.metrics?.current_expected_cash_cents ??
                      params.activeShift.expected_cash_cents
                  )
                : 'SHIFT CLOSED'
            }
          </div>
          <div class="ess-kpi-meta">
            ${
              params.activeShift
                ? `Opened ${formatTimeShort(params.activeShift.opening_time)} • Float ${formatCurrency(params.activeShift.opening_float_cents)}`
                : 'Open a shift before ringing cash sales'
            }
          </div>
        </div>

        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">M-Pesa Collections</div>
          <div class="ess-kpi-value">${formatCurrency(mpesaRevenueCents)}</div>
          <div class="ess-kpi-meta">Cash: ${formatCurrency(cashRevenueCents)}</div>
        </div>
      </div>

      <!-- Payment Breakdown & Notifications Row -->
      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Payment Method Breakdown</h3>
            <span class="ess-badge ess-badge-neutral">Authoritative Ledger</span>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row">
              <span>Cash Drawer Collections</span>
              <strong>${formatCurrency(cashRevenueCents)}</strong>
            </div>
            <div class="ess-kv-row">
              <span>M-Pesa STK / Verified Till</span>
              <strong style="color:var(--ess-primary);">${formatCurrency(mpesaRevenueCents)}</strong>
            </div>
            <div class="ess-kv-row">
              <span>Card &amp; Split Payments</span>
              <strong>${formatCurrency(otherRevenueCents)}</strong>
            </div>
            <div class="ess-kv-row" style="border-top:1px solid var(--ess-border);padding-top:10px;">
              <strong>Total Settled Revenue</strong>
              <strong style="font-size:16px;">${formatCurrency(totalRevenueCents)}</strong>
            </div>
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Important Cashier Notifications</h3>
            <span class="ess-badge ess-badge-orange">Live</span>
          </div>
          <div class="ess-stack-sm">
            ${
              params.pendingTableBills.length > 0
                ? `<div class="ess-alert-card ess-alert-orange">
                    <strong>${params.pendingTableBills.length} Table Bill(s) Ready for Settlement</strong>
                    <p>Waiter requested customer bill for ${escapeHtml(params.pendingTableBills[0].tableName)} (${formatCurrency(params.pendingTableBills[0].totalCents)}).</p>
                  </div>`
                : ''
            }
            ${
              !params.activeShift
                ? `<div class="ess-alert-card ess-alert-warning">
                    <strong>Cashier Shift Not Open</strong>
                    <p>Open your shift and declare your starting float in the Shift &amp; Cash Drawer screen.</p>
                  </div>`
                : `<div class="ess-alert-card ess-alert-neutral">
                    <strong>Active Shift Running</strong>
                    <p>All cash sales are automatically tracked against your opening float for end-of-shift reconciliation.</p>
                  </div>`
            }
          </div>
        </div>
      </div>

      <!-- Recent Transactions Table -->
      <div class="ess-card">
        <div class="ess-card-header">
          <h3 class="ess-card-title">Recent Completed Transactions</h3>
          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="dash-view-all-sales">
            View Full History &amp; Receipts
          </button>
        </div>
        ${
          completedSales.length === 0
            ? renderEmptyState({
                title: 'No Sales Recorded Today',
                description: 'Launch the POS billing screen to ring up your first customer order.',
                actionLabel: 'Open POS Billing',
                actionId: 'empty-open-pos',
              })
            : `
          <div class="ess-table-wrap">
            <table class="ess-table">
              <thead>
                <tr>
                  <th>Receipt #</th>
                  <th>Time</th>
                  <th>Payment</th>
                  <th>Discount</th>
                  <th>Total Paid</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                ${completedSales
                  .slice(0, 6)
                  .map(
                    (s) => `
                  <tr>
                    <td><strong>${escapeHtml(s.receipt_number)}</strong></td>
                    <td>${formatDateTime(s.created_at)}</td>
                    <td><span class="ess-badge ${s.payment_method === 'MPESA' ? 'ess-badge-orange' : 'ess-badge-neutral'}">${escapeHtml(s.payment_method)}</span></td>
                    <td>${formatCurrency(s.discount_cents)}</td>
                    <td><strong>${formatCurrency(s.total_cents)}</strong></td>
                    <td><span class="ess-badge ess-badge-success">${escapeHtml(s.status)}</span></td>
                    <td>
                      <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-receipt-sale-id="${escapeHtml(s.id)}">
                        Receipt
                      </button>
                    </td>
                  </tr>
                `
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        `
        }
      </div>
    </div>
  `;

  viewport.querySelector('#dash-go-pos')?.addEventListener('click', () => params.onNavigateTab('pos-sale'));
  viewport.querySelector('#empty-open-pos')?.addEventListener('click', () => params.onNavigateTab('pos-sale'));
  viewport.querySelector('#dash-go-shift')?.addEventListener('click', () => params.onNavigateTab('shift-drawer'));
  viewport.querySelector('#dash-view-all-sales')?.addEventListener('click', () => params.onNavigateTab('completed-sales'));
  viewport.querySelector('#dash-quick-qr')?.addEventListener('click', () => params.onNavigateTab('incoming-qr-orders'));
  viewport.querySelector('#dash-alert-incoming-qr')?.addEventListener('click', () => params.onNavigateTab('incoming-qr-orders'));

  viewport.querySelectorAll('[data-receipt-sale-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-receipt-sale-id');
      const sale = params.sales.find((s) => s.id === id);
      if (sale) openReceiptPreviewModal(sale);
    });
  });
}

// ============================================================================
// 2. NEW SALE (POS BILLING) & PAYMENT WORKFLOW
// ============================================================================

function renderPosBillingScreen(
  viewport: HTMLElement,
  params: {
    categories: CategoryRecord[];
    products: ProductRecord[];
    activeShift: CashierShiftRecord | null;
    primaryBranch?: BranchRecord;
    retailLocation?: StockLocationRecord;
    pendingBillsCount: number;
    incomingQrCount?: number;
    onNavigateTab: (tab: string) => void;
    onSaleCompleted: () => void;
  }
) {
  const renderPos = () => {
    const filteredProducts = params.products.filter((p) => {
      if (
        cartState.selectedCategoryId !== 'ALL' &&
        p.category_id !== cartState.selectedCategoryId
      ) {
        return false;
      }
      if (cartState.searchQuery.trim()) {
        const q = cartState.searchQuery.toLowerCase();
        const nameMatch = p.name.toLowerCase().includes(q);
        const skuMatch = p.variants.some(
          (v) =>
            v.sku.toLowerCase().includes(q) ||
            (v.barcode && v.barcode.toLowerCase().includes(q))
        );
        return nameMatch || skuMatch;
      }
      return true;
    });

    const subtotalCents = cartState.items.reduce(
      (sum, item) => sum + item.unitPriceCents * item.quantity,
      0
    );
    const totalDiscountCents = cartState.items.reduce((sum, item) => sum + item.discountCents, 0);
    const totalPayableCents = Math.max(0, subtotalCents - totalDiscountCents);

    viewport.innerHTML = `
      <div class="ess-pos-layout">
        <!-- Left Catalog Column -->
        <div class="ess-pos-catalog">
          <!-- Search & Shift Bar -->
          <div class="ess-card ess-pos-toolbar">
            <div class="ess-pos-search-wrap">
              <input
                type="search"
                id="pos-product-search"
                class="ess-input"
                placeholder="Search menu item, SKU or scan barcode (e.g. GRL-001, Nyama Choma)..."
                value="${escapeHtml(cartState.searchQuery)}"
              />
            </div>
            <div class="ess-pos-toolbar-meta">
              ${
                (params.incomingQrCount ?? 0) > 0
                  ? `<button type="button" class="ess-btn ess-btn-sm" id="pos-goto-incoming-qr-btn" style="background:#f59e0b;color:#fff;border:none;font-weight:600;">
                      🛎️ Incoming QR (${params.incomingQrCount})
                    </button>`
                  : ''
              }
              ${
                params.pendingBillsCount > 0
                  ? `<button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="pos-load-pending-btn">
                      Active Bills (${params.pendingBillsCount})
                    </button>`
                  : ''
              }
              <span class="ess-badge ${params.activeShift ? 'ess-badge-success' : 'ess-badge-warning'}">
                ${params.activeShift ? 'Shift Active' : 'No Open Shift'}
              </span>
            </div>
          </div>

          <!-- Category Filter Pills -->
          <div class="ess-category-pills" role="tablist" aria-label="Menu Categories">
            <button
              type="button"
              class="ess-pill ${cartState.selectedCategoryId === 'ALL' ? 'active' : ''}"
              data-cat-id="ALL"
            >
              All Menu Items (${params.products.length})
            </button>
            ${params.categories
              .map(
                (cat) => `
              <button
                type="button"
                class="ess-pill ${cartState.selectedCategoryId === cat.id ? 'active' : ''}"
                data-cat-id="${escapeHtml(cat.id)}"
              >
                ${escapeHtml(cat.name)}
              </button>
            `
              )
              .join('')}
          </div>

          <!-- Product Grid -->
          ${
            filteredProducts.length === 0
              ? renderEmptyState({
                  title: 'No Matching Menu Products',
                  description: 'Try clearing your search filter or selecting another category.',
                  actionLabel: 'Reset Filter',
                  actionId: 'pos-reset-filter',
                })
              : `
            <div class="ess-product-grid">
              ${filteredProducts
                .map((product) => {
                  const v: ProductVariantRecord | undefined = product.variants[0];
                  if (!v) return '';
                  const stock = Number(v.total_stock ?? 0);
                  const isOut = stock <= 0;
                  const isLow = stock > 0 && stock <= 5;
                  const initials = product.name
                    .split(' ')
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase();

                  return `
                    <div class="ess-product-card ${isOut ? 'ess-product-disabled' : ''}" data-add-variant="${escapeHtml(v.id)}">
                      ${
                        product.image_url
                          ? `
                        <div class="ess-product-img-wrap">
                          <img src="${escapeHtml(product.image_url)}" alt="${escapeHtml(product.name)}" class="ess-product-img" loading="lazy" onerror="this.parentElement.style.display='none';" />
                          <span class="ess-badge ${
                            isOut
                              ? 'ess-badge-danger'
                              : isLow
                              ? 'ess-badge-warning'
                              : 'ess-badge-success'
                          } ess-product-badge-overlay">
                            ${isOut ? 'Out' : isLow ? `Low: ${stock}` : `${stock} in stock`}
                          </span>
                        </div>
                      `
                          : `
                        <div class="ess-product-card-top">
                          <div class="ess-product-thumb">${escapeHtml(initials)}</div>
                          <span class="ess-badge ${
                            isOut
                              ? 'ess-badge-danger'
                              : isLow
                              ? 'ess-badge-warning'
                              : 'ess-badge-success'
                          }">
                            ${isOut ? 'Out of Stock' : isLow ? `Low: ${stock} left` : `${stock} in stock`}
                          </span>
                        </div>
                      `
                      }
                      <div class="ess-product-name">${escapeHtml(product.name)}</div>
                      <div class="ess-product-sku">SKU: ${escapeHtml(v.sku)} &bull; ${escapeHtml(v.variant_name)}</div>
                      <div class="ess-product-card-bottom">
                        <div class="ess-product-price">${formatCurrency(v.retail_price_cents)}</div>
                        <button
                          type="button"
                          class="ess-btn ${isOut ? 'ess-btn-secondary' : 'ess-btn-primary'} ess-btn-sm"
                          ${isOut ? 'disabled' : ''}
                        >
                          ${isOut ? 'Unavailable' : '+ Add'}
                        </button>
                      </div>
                    </div>
                  `;
                })
                .join('')}
            </div>
          `
          }
        </div>

        <!-- Right Order Cart & Billing Panel -->
        <aside class="ess-pos-cart" aria-label="Current Customer Bill">
          <div class="ess-pos-cart-header">
            <div>
              <h3 class="ess-card-title">Current Customer Bill</h3>
              <div class="ess-card-subtitle">
                ${
                  cartState.linkedTableName
                    ? `<span class="ess-badge ess-badge-orange">Table Bill: ${escapeHtml(cartState.linkedTableName)}</span>`
                    : 'Direct Counter / Walk-In Order'
                }
              </div>
            </div>
            ${
              cartState.items.length > 0
                ? `<button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="pos-clear-cart">Clear</button>`
                : ''
            }
          </div>

          <div class="ess-pos-cart-items">
            ${
              cartState.items.length === 0
                ? `<div class="ess-cart-empty">
                    <p><strong>Cart is Empty</strong></p>
                    <span>Tap any available product card or load a pending waiter table bill.</span>
                  </div>`
                : cartState.items
                    .map(
                      (line, idx) => `
                  <div class="ess-cart-line">
                    <div class="ess-cart-line-main">
                      <div>
                        <div class="ess-cart-line-title">${escapeHtml(line.productName)}</div>
                        <div class="ess-cart-line-meta">
                          ${formatCurrency(line.unitPriceCents)} &times; ${line.quantity}
                          ${line.discountCents > 0 ? `<span style="color:var(--ess-primary);">(-${formatCurrency(line.discountCents)})</span>` : ''}
                        </div>
                      </div>
                      <div class="ess-cart-line-total">
                        ${formatCurrency(Math.max(0, line.unitPriceCents * line.quantity - line.discountCents))}
                      </div>
                    </div>

                    <div class="ess-cart-line-controls">
                      <div class="ess-qty-stepper">
                        <button type="button" class="ess-qty-btn" data-cart-dec="${idx}" aria-label="Decrease quantity">&minus;</button>
                        <span class="ess-qty-val">${line.quantity}</span>
                        <button type="button" class="ess-qty-btn" data-cart-inc="${idx}" aria-label="Increase quantity">+</button>
                      </div>
                      <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" data-cart-note="${idx}">
                        ${line.notes ? 'Edit Note' : '+ Note / Discount'}
                      </button>
                      <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" style="color:var(--ess-danger-text);" data-cart-rm="${idx}">
                        Remove
                      </button>
                    </div>
                    ${
                      line.notes
                        ? `<div class="ess-cart-note-pill">Note: ${escapeHtml(line.notes)}</div>`
                        : ''
                    }
                  </div>
                `
                    )
                    .join('')
            }
          </div>

          <!-- Provisional Totals & Checkout Action -->
          <div class="ess-pos-cart-footer">
            <div class="ess-kv-row">
              <span>Provisional Subtotal</span>
              <strong>${formatCurrency(subtotalCents)}</strong>
            </div>
            <div class="ess-kv-row">
              <span>Authorized Discounts</span>
              <strong>&minus; ${formatCurrency(totalDiscountCents)}</strong>
            </div>
            <div class="ess-kv-row">
              <span>Tax (VAT Inclusive / Configured)</span>
              <strong>KES 0.00</strong>
            </div>
            <div class="ess-pos-total-row">
              <span>Total Payable</span>
              <strong>${formatCurrency(totalPayableCents)}</strong>
            </div>

            <button
              type="button"
              class="ess-btn ess-btn-primary ess-btn-lg ess-btn-block"
              id="pos-proceed-payment-btn"
              ${cartState.items.length === 0 ? 'disabled' : ''}
            >
              Proceed to Payment (${formatCurrency(totalPayableCents)})
            </button>
          </div>
        </aside>
      </div>
    `;

    // Bind Search
    const searchInput = viewport.querySelector('#pos-product-search') as HTMLInputElement | null;
    searchInput?.addEventListener('input', () => {
      cartState.searchQuery = searchInput.value;
      renderPos();
      const el = viewport.querySelector('#pos-product-search') as HTMLInputElement | null;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });

    // Bind Category Pills
    viewport.querySelectorAll('[data-cat-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        cartState.selectedCategoryId = btn.getAttribute('data-cat-id') || 'ALL';
        renderPos();
      });
    });

    viewport.querySelector('#pos-reset-filter')?.addEventListener('click', () => {
      cartState.searchQuery = '';
      cartState.selectedCategoryId = 'ALL';
      renderPos();
    });

    viewport.querySelector('#pos-load-pending-btn')?.addEventListener('click', () => {
      params.onNavigateTab('pending-bills');
    });
    viewport.querySelector('#pos-goto-incoming-qr-btn')?.addEventListener('click', () => {
      params.onNavigateTab('incoming-qr-orders');
    });

    // Bind Add to Cart
    viewport.querySelectorAll('[data-add-variant]').forEach((card) => {
      card.addEventListener('click', () => {
        const variantId = card.getAttribute('data-add-variant');
        if (!variantId) return;

        let foundProduct: ProductRecord | undefined;
        let foundVariant: ProductVariantRecord | undefined;

        for (const p of params.products) {
          const v = p.variants.find((item) => item.id === variantId);
          if (v) {
            foundProduct = p;
            foundVariant = v;
            break;
          }
        }

        if (!foundProduct || !foundVariant) return;
        const available = Number(foundVariant.total_stock ?? 0);

        if (available <= 0) {
          showToast({
            title: 'Product Out of Stock',
            message: `${foundProduct.name} has 0 available stock and cannot be sold.`,
            type: 'danger',
          });
          return;
        }

        const existing = cartState.items.find((i) => i.variantId === variantId);
        if (existing) {
          if (existing.quantity + 1 > available) {
            showToast({
              title: 'Stock Limit Reached',
              message: `Only ${available} units of ${foundProduct.name} are available on hand.`,
              type: 'warning',
            });
            return;
          }
          existing.quantity += 1;
        } else {
          cartState.items.push({
            variantId: foundVariant.id,
            productId: foundProduct.id,
            productName: foundProduct.name,
            variantName: foundVariant.variant_name,
            sku: foundVariant.sku,
            unitPriceCents: foundVariant.retail_price_cents,
            maxAvailableStock: available,
            quantity: 1,
            discountCents: 0,
            notes: '',
          });
        }
        renderPos();
      });
    });

    // Bind Cart Controls
    viewport.querySelector('#pos-clear-cart')?.addEventListener('click', () => {
      cartState.items = [];
      cartState.linkedTableOrderId = null;
      cartState.linkedTableName = null;
      renderPos();
    });

    viewport.querySelectorAll('[data-cart-inc]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-cart-inc'));
        const item = cartState.items[idx];
        if (!item) return;
        if (item.quantity + 1 > item.maxAvailableStock) {
          showToast({
            title: 'Insufficient Stock',
            message: `Maximum available stock (${item.maxAvailableStock}) reached.`,
            type: 'warning',
          });
          return;
        }
        item.quantity += 1;
        renderPos();
      });
    });

    viewport.querySelectorAll('[data-cart-dec]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-cart-dec'));
        const item = cartState.items[idx];
        if (!item) return;
        if (item.quantity <= 1) {
          cartState.items.splice(idx, 1);
        } else {
          item.quantity -= 1;
        }
        renderPos();
      });
    });

    viewport.querySelectorAll('[data-cart-rm]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-cart-rm'));
        cartState.items.splice(idx, 1);
        renderPos();
      });
    });

    viewport.querySelectorAll('[data-cart-note]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-cart-note'));
        const item = cartState.items[idx];
        if (!item) return;

        openModal({
          title: `Item Note & Discount — ${item.productName}`,
          subtitle: 'Cashier discounts above KES 200 are logged for Manager review',
          width: 'sm',
          bodyHtml: `
            <div class="ess-form">
              <div class="ess-form-group">
                <label class="ess-label">Preparation / Billing Note</label>
                <input type="text" id="line-item-note" class="ess-input" value="${escapeHtml(item.notes)}" placeholder="e.g. No chilli, takeaway pack" />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Line Discount (KES)</label>
                <input type="number" id="line-item-discount" class="ess-input" min="0" max="${(item.unitPriceCents * item.quantity) / 100}" value="${item.discountCents / 100}" />
              </div>
            </div>
          `,
          footerHtml: `
            <button type="button" class="ess-btn ess-btn-secondary" id="line-modal-cancel">Cancel</button>
            <button type="button" class="ess-btn ess-btn-primary" id="line-modal-save">Apply to Item</button>
          `,
          onMount: (root, close) => {
            root.querySelector('#line-modal-cancel')?.addEventListener('click', close);
            root.querySelector('#line-modal-save')?.addEventListener('click', () => {
              const noteVal = (root.querySelector('#line-item-note') as HTMLInputElement).value.trim();
              const discKes = Number((root.querySelector('#line-item-discount') as HTMLInputElement).value || 0);
              const maxKes = (item.unitPriceCents * item.quantity) / 100;
              item.notes = noteVal;
              item.discountCents = Math.min(Math.max(0, Math.round(discKes * 100)), Math.round(maxKes * 100));
              close();
              renderPos();
            });
          },
        });
      });
    });

    // Bind Proceed to Payment Modal
    viewport.querySelector('#pos-proceed-payment-btn')?.addEventListener('click', () => {
      if (!params.primaryBranch || !params.retailLocation) {
        showToast({
          title: 'Configuration Missing',
          message: 'No active branch or retail stock location found for this tenant.',
          type: 'danger',
        });
        return;
      }

      openPaymentSettlementModal({
        totalPayableCents,
        branchId: params.primaryBranch.id,
        stockLocationId: params.retailLocation.id,
        shiftId: params.activeShift?.id,
        onSuccess: (completedSale) => {
          if (cartState.linkedTableOrderId) {
            restaurantMockAdapter.advanceOrderStatus(
              apiClient.getEffectiveTenantId(),
              cartState.linkedTableOrderId,
              'PAID',
              apiClient.getUser()?.firstName || 'Cashier'
            );
          }
          cartState.items = [];
          cartState.linkedTableOrderId = null;
          cartState.linkedTableName = null;
          params.onSaleCompleted();
          openReceiptPreviewModal(completedSale);
        },
      });
    });
  };

  renderPos();
}

function openPaymentSettlementModal(params: {
  totalPayableCents: number;
  branchId: string;
  stockLocationId: string;
  shiftId?: string;
  onSuccess: (sale: SaleRecord) => void;
}) {
  let selectedMethod: 'CASH' | 'MPESA' | 'CARD' = 'CASH';
  let mpesaVerificationState: 'IDLE' | 'PROMPT_SENT' | 'VERIFIED' = 'IDLE';
  let verifiedMpesaRef = '';
  // Generate one idempotency key per checkout session to guarantee zero double-charging
  const idempotencyKey = `pos_chk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  openModal({
    title: 'Settle Customer Bill & Finalize Sale',
    subtitle: `Idempotency Token: ${idempotencyKey}`,
    width: 'md',
    bodyHtml: `<div id="payment-modal-dynamic"></div>`,
    onMount: (root, close) => {
      const container = root.querySelector('#payment-modal-dynamic') as HTMLElement;

      const renderPaymentForm = () => {
        const defaultTenderKes = Math.ceil(params.totalPayableCents / 100);
        container.innerHTML = `
          <div class="ess-stack-md">
            <div class="ess-payment-summary-banner">
              <span>Authoritative Amount Due</span>
              <strong>${formatCurrency(params.totalPayableCents)}</strong>
            </div>

            <div class="ess-payment-methods">
              <button type="button" class="ess-method-tab ${selectedMethod === 'CASH' ? 'active' : ''}" data-pay-method="CASH">
                <strong>Cash Tender</strong>
                <span>Instant Drawer Change</span>
              </button>
              <button type="button" class="ess-method-tab ${selectedMethod === 'MPESA' ? 'active' : ''}" data-pay-method="MPESA">
                <strong>M-Pesa STK / Till</strong>
                <span>Verified Confirmation</span>
              </button>
              <button type="button" class="ess-method-tab ${selectedMethod === 'CARD' ? 'active' : ''}" data-pay-method="CARD">
                <strong>Card / PDQ</strong>
                <span>Terminal Reference</span>
              </button>
            </div>

            ${
              selectedMethod === 'CASH'
                ? `
              <div class="ess-form">
                <div class="ess-form-group">
                  <label class="ess-label">Cash Received from Customer (KES) *</label>
                  <input
                    type="number"
                    id="pay-cash-tendered"
                    class="ess-input ess-input-lg"
                    min="${defaultTenderKes}"
                    step="10"
                    value="${defaultTenderKes}"
                  />
                </div>
                <div class="ess-quick-cash-row">
                  <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-quick-kes="${defaultTenderKes}">Exact (${defaultTenderKes})</button>
                  <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-quick-kes="${Math.ceil(defaultTenderKes / 500) * 500}">KES ${Math.ceil(defaultTenderKes / 500) * 500}</button>
                  <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-quick-kes="${Math.ceil(defaultTenderKes / 1000) * 1000 + 1000}">KES ${Math.ceil(defaultTenderKes / 1000) * 1000 + 1000}</button>
                </div>
                <div class="ess-change-box" id="pay-change-display">
                  <span>Change Due to Customer:</span>
                  <strong>KES 0.00</strong>
                </div>
              </div>
            `
                : selectedMethod === 'MPESA'
                ? `
              <div class="ess-form">
                <div class="ess-alert-card ess-alert-neutral">
                  <strong>Financial Safety Policy</strong>
                  <p>M-Pesa transactions require gateway callback verification or a confirmed M-Pesa receipt code before completing the sale.</p>
                </div>
                <div class="ess-grid-2">
                  <div class="ess-form-group">
                    <label class="ess-label">Customer M-Pesa Phone</label>
                    <input type="tel" id="pay-mpesa-phone" class="ess-input" value="254712345678" placeholder="2547XXXXXXXX" />
                  </div>
                  <div class="ess-form-group">
                    <label class="ess-label">M-Pesa Receipt Reference *</label>
                    <input type="text" id="pay-mpesa-ref" class="ess-input" value="${escapeHtml(verifiedMpesaRef)}" placeholder="e.g. QKA84Z91LP" />
                  </div>
                </div>
                <div style="display:flex;gap:8px;align-items:center;">
                  <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="pay-mpesa-stk-btn">
                    ${mpesaVerificationState === 'IDLE' ? '1. Dispatch STK Prompt & Verify' : 'STK Confirmed by Gateway'}
                  </button>
                  <span class="ess-badge ${
                    mpesaVerificationState === 'VERIFIED'
                      ? 'ess-badge-success'
                      : mpesaVerificationState === 'PROMPT_SENT'
                      ? 'ess-badge-warning'
                      : 'ess-badge-neutral'
                  }" id="pay-mpesa-status-badge">
                    Status: ${mpesaVerificationState}
                  </span>
                </div>
              </div>
            `
                : `
              <div class="ess-form">
                <div class="ess-form-group">
                  <label class="ess-label">PDQ / Card Approval Code *</label>
                  <input type="text" id="pay-card-ref" class="ess-input" placeholder="Enter 6-digit terminal approval code" />
                </div>
              </div>
            `
            }

            <div id="pay-modal-error" class="ess-form-error" style="display:none;"></div>

            <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:8px;">
              <button type="button" class="ess-btn ess-btn-secondary" id="pay-cancel-btn">Cancel</button>
              <button type="button" class="ess-btn ess-btn-primary ess-btn-lg" id="pay-confirm-submit-btn">
                Complete Sale &amp; Issue Receipt
              </button>
            </div>
          </div>
        `;

        container.querySelectorAll('[data-pay-method]').forEach((btn) => {
          btn.addEventListener('click', () => {
            selectedMethod = btn.getAttribute('data-pay-method') as any;
            renderPaymentForm();
          });
        });

        // Live Cash Change Calculator
        const cashInput = container.querySelector('#pay-cash-tendered') as HTMLInputElement | null;
        const changeDisplay = container.querySelector('#pay-change-display') as HTMLElement | null;
        const updateChange = () => {
          if (!cashInput || !changeDisplay) return;
          const tenderedCents = Math.round(Number(cashInput.value || 0) * 100);
          const diff = tenderedCents - params.totalPayableCents;
          changeDisplay.innerHTML = `
            <span>Change Due to Customer:</span>
            <strong style="color:${diff < 0 ? 'var(--ess-danger-text)' : 'var(--ess-success-text)'};">
              ${diff < 0 ? `Short by ${formatCurrency(Math.abs(diff))}` : formatCurrency(diff)}
            </strong>
          `;
        };
        cashInput?.addEventListener('input', updateChange);
        container.querySelectorAll('[data-quick-kes]').forEach((b) => {
          b.addEventListener('click', () => {
            if (cashInput) {
              cashInput.value = b.getAttribute('data-quick-kes') || '';
              updateChange();
            }
          });
        });

        // M-Pesa STK Dispatch & Gateway Verification Simulation
        const stkBtn = container.querySelector('#pay-mpesa-stk-btn') as HTMLButtonElement | null;
        stkBtn?.addEventListener('click', () => {
          if (!stkBtn) return;
          stkBtn.disabled = true;
          stkBtn.textContent = 'Waiting for M-Pesa Callback...';
          mpesaVerificationState = 'PROMPT_SENT';
          window.setTimeout(() => {
            mpesaVerificationState = 'VERIFIED';
            verifiedMpesaRef = `SGK${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
            renderPaymentForm();
            showToast({
              title: 'M-Pesa Payment Confirmed',
              message: `Reference ${verifiedMpesaRef} verified.`,
              type: 'success',
            });
          }, 900);
        });

        container.querySelector('#pay-cancel-btn')?.addEventListener('click', close);

        const submitBtn = container.querySelector('#pay-confirm-submit-btn') as HTMLButtonElement | null;
        submitBtn?.addEventListener('click', async () => {
          const errBox = container.querySelector('#pay-modal-error') as HTMLElement;
          errBox.style.display = 'none';

          let cashTenderedCents: number | undefined;
          let mpesaRef: string | undefined;

          if (selectedMethod === 'CASH') {
            const val = Number((container.querySelector('#pay-cash-tendered') as HTMLInputElement)?.value || 0);
            cashTenderedCents = Math.round(val * 100);
            if (cashTenderedCents < params.totalPayableCents) {
              errBox.textContent = `Cash tendered (${formatCurrency(cashTenderedCents)}) is less than the bill (${formatCurrency(params.totalPayableCents)}).`;
              errBox.style.display = 'block';
              return;
            }
          } else if (selectedMethod === 'MPESA') {
            mpesaRef = (container.querySelector('#pay-mpesa-ref') as HTMLInputElement)?.value.trim();
            if (!mpesaRef || mpesaRef.length < 5) {
              errBox.textContent = 'Please dispatch STK push or enter a verified M-Pesa transaction reference before completing the sale.';
              errBox.style.display = 'block';
              return;
            }
          } else if (selectedMethod === 'CARD') {
            const cardRef = (container.querySelector('#pay-card-ref') as HTMLInputElement)?.value.trim();
            if (!cardRef) {
              errBox.textContent = 'Please enter the PDQ card terminal approval reference.';
              errBox.style.display = 'block';
              return;
            }
          }

          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Executing Atomic PostgreSQL / Ledger Transaction...';
          }

          try {
            const checkoutRes = await apiClient.checkoutSale({
              branchId: params.branchId,
              stockLocationId: params.stockLocationId,
              shiftId: params.shiftId,
              items: cartState.items.map((i) => ({
                variantId: i.variantId,
                quantity: i.quantity,
                discountCents: i.discountCents,
              })),
              paymentMethod: selectedMethod,
              cashTenderedCents,
              mpesaReference: mpesaRef,
              idempotencyKey,
            });

            showToast({
              title: 'Sale Completed & Stock Deducted',
              message: `Receipt ${checkoutRes.receipt.receiptNumber}`,
              type: 'success',
            });
            close();
            params.onSuccess({
              ...checkoutRes.sale,
              items: checkoutRes.items,
            });
          } catch (err: any) {
            errBox.textContent = err?.message || 'Checkout transaction failed.';
            errBox.style.display = 'block';
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = 'Complete Sale & Issue Receipt';
            }
          }
        });
      };

      renderPaymentForm();
    },
  });
}

// ============================================================================
// 2.5 INCOMING QR ORDERS QUEUE (Cashier Confirmation & Live Chime)
// ============================================================================

let qrPollingTimer: any = null;

function playOrderChime() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    // Pleasant dual-tone chime (F#5 -> B5)
    osc.frequency.setValueAtTime(739.99, ctx.currentTime);
    osc.frequency.setValueAtTime(987.77, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  } catch {}
}

function renderIncomingQrOrdersScreen(
  viewport: HTMLElement,
  params: {
    incomingOrders: QrOrderSubmissionRecord[];
    onRefresh: () => void;
    onNavigateTab: (tab: string) => void;
  }
) {
  // Clear any existing timer to prevent duplicates
  if (qrPollingTimer) {
    clearInterval(qrPollingTimer);
    qrPollingTimer = null;
  }

  let knownOrderCount = params.incomingOrders.length;

  // Real-time auto-polling every 8 seconds for new QR orders
  qrPollingTimer = setInterval(async () => {
    try {
      if (!viewport.isConnected) {
        clearInterval(qrPollingTimer);
        qrPollingTimer = null;
        return;
      }
      const freshOrders = await apiClient.listIncomingQrOrders('PENDING');
      if (freshOrders.length > knownOrderCount) {
        playOrderChime();
        showToast({
          title: '🛎️ New QR Order Arrived!',
          message: `${freshOrders.length - knownOrderCount} new table order(s) awaiting confirmation.`,
          type: 'info',
        });
        clearInterval(qrPollingTimer);
        params.onRefresh();
      } else if (freshOrders.length !== knownOrderCount) {
        clearInterval(qrPollingTimer);
        params.onRefresh();
      }
      knownOrderCount = freshOrders.length;
    } catch {}
  }, 8000);
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <div style="display:flex;align-items:center;gap:10px;">
            <h2 class="ess-section-title">Incoming QR Orders Queue</h2>
            <span class="ess-badge ess-badge-orange" style="font-size:13px;padding:4px 10px;">
              ${params.incomingOrders.length} Pending
            </span>
          </div>
          <p class="ess-section-desc">
            Customer orders submitted via Table QR codes awaiting cashier review and confirmation before billing and kitchen prep
          </p>
        </div>
        <div style="display:flex;gap:8px;">
          <button type="button" class="ess-btn ess-btn-secondary" id="qr-refresh-btn">
            &#x21bb; Refresh Queue
          </button>
          <button type="button" class="ess-btn ess-btn-primary" id="qr-back-pos">
            Back to POS Screen
          </button>
        </div>
      </div>

      ${
        params.incomingOrders.length === 0
          ? renderEmptyState({
              title: 'No Incoming QR Orders',
              description: 'All table QR orders have been reviewed. New orders will appear here automatically when diners submit from their tables.',
            })
          : `
        <div class="ess-grid-2">
          ${params.incomingOrders
            .map(
              (order) => `
            <div class="ess-card" style="border: 1px solid var(--ess-border); border-top: 4px solid #f59e0b; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);" data-qr-card="${order.id}">
              <div class="ess-card-header" style="align-items:flex-start;">
                <div>
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
                    <span class="ess-badge ess-badge-orange">Pending Confirmation</span>
                    <span class="ess-badge ess-badge-blue">Source: QR table order</span>
                  </div>
                  <h3 class="ess-card-title" style="font-size:18px;">Table ${escapeHtml(order.table_number)}</h3>
                  <div class="ess-card-subtitle" style="margin-top:2px;">
                    Order <strong>#${escapeHtml(order.submission_reference)}</strong> &bull; ${formatDateTime(order.created_at)}
                    ${order.customer_name ? ` &bull; Guest: ${escapeHtml(order.customer_name)}` : ''}
                  </div>
                </div>
                <div style="text-align:right;">
                  <span style="font-size:11px;color:var(--ess-text-muted);display:block;">Payment Status</span>
                  <span style="color:#ef4444;font-weight:700;font-size:13px;">Not received</span>
                </div>
              </div>

              ${order.customer_notes ? `
                <div style="background:#fffbeb;border-left:3px solid #f59e0b;padding:8px 12px;margin:10px 0;font-size:12px;border-radius:4px;">
                  <strong>Customer Note:</strong> ${escapeHtml(order.customer_notes)}
                </div>
              ` : ''}

              <div class="ess-stack-sm" style="margin: 14px 0;">
                <div style="display:grid;grid-template-columns: 2fr 1fr 1fr;font-size:11px;font-weight:700;color:var(--ess-text-muted);border-bottom:1px solid var(--ess-border);padding-bottom:6px;">
                  <span>ITEM</span>
                  <span style="text-align:center;">QTY &times; PRICE</span>
                  <span style="text-align:right;">AMOUNT</span>
                </div>
                ${(order.items || [])
                  .map(
                    (item) => `
                  <div style="display:grid;grid-template-columns: 2fr 1fr 1fr;align-items:center;padding:6px 0;font-size:13px;border-bottom:1px dashed var(--ess-border);">
                    <div>
                      <strong style="color:var(--ess-text);">${escapeHtml(item.product_name)}</strong>
                      ${item.notes ? `<div style="font-size:11px;color:var(--ess-text-muted);font-style:italic;">Note: ${escapeHtml(item.notes)}</div>` : ''}
                    </div>
                    <span style="text-align:center;color:var(--ess-text-muted);font-size:12px;">
                      ${item.quantity}&times; ${formatCurrency(item.unit_price_cents)}
                    </span>
                    <strong style="text-align:right;color:var(--ess-text);">
                      ${formatCurrency(item.total_price_cents)}
                    </strong>
                  </div>
                `
                  )
                  .join('')}
                <div class="ess-kv-row" style="padding-top:10px;font-size:15px;">
                  <strong>Total Bill Payable</strong>
                  <strong style="color:var(--ess-primary);font-size:18px;">${formatCurrency(order.total_cents)}</strong>
                </div>
              </div>

              <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:16px;">
                <button type="button" class="ess-btn ess-btn-success ess-btn-block" data-accept-qr-id="${escapeHtml(order.id)}" data-order-ref="${escapeHtml(order.submission_reference)}" data-table="${escapeHtml(order.table_number)}">
                  &#x2713; Accept Order
                </button>
                <button type="button" class="ess-btn ess-btn-danger ess-btn-block" data-reject-qr-id="${escapeHtml(order.id)}" data-order-ref="${escapeHtml(order.submission_reference)}" data-table="${escapeHtml(order.table_number)}">
                  &#x2715; Reject
                </button>
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
      }
    </div>
  `;

  viewport.querySelector('#qr-refresh-btn')?.addEventListener('click', () => {
    if (qrPollingTimer) clearInterval(qrPollingTimer);
    params.onRefresh();
  });
  viewport.querySelector('#qr-back-pos')?.addEventListener('click', () => {
    if (qrPollingTimer) clearInterval(qrPollingTimer);
    params.onNavigateTab('pos-sale');
  });

  // Handle Accept Order
  viewport.querySelectorAll('[data-accept-qr-id]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-accept-qr-id')!;
      const ref = btn.getAttribute('data-order-ref') || '';
      const table = btn.getAttribute('data-table') || '';
      
      const buttonElem = btn as HTMLButtonElement;
      buttonElem.disabled = true;
      buttonElem.textContent = 'Accepting...';

      try {
        await apiClient.acceptQrOrder(id);
        showToast({
          title: 'Order Accepted',
          message: `Order #${ref} for Table ${table} accepted! Active bill created.`,
          type: 'success',
        });
        params.onRefresh();
      } catch (err: any) {
        buttonElem.disabled = false;
        buttonElem.textContent = '✓ Accept Order';
        showToast({
          title: 'Failed to Accept Order',
          message: err?.message || 'Could not accept order.',
          type: 'error',
        });
      }
    });
  });

  // Handle Reject Order
  viewport.querySelectorAll('[data-reject-qr-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-reject-qr-id')!;
      const ref = btn.getAttribute('data-order-ref') || '';
      const table = btn.getAttribute('data-table') || '';

      openModal({
        title: `Reject QR Order #${ref}`,
        subtitle: `Table ${table} · Enter reason to notify customer`,
        width: 'md',
        bodyHtml: `
          <div class="ess-form-group">
            <label class="ess-label">Rejection Reason</label>
            <select class="ess-select" id="reject-preset-select" style="margin-bottom:8px;">
              <option value="Item temporarily out of stock">Item temporarily out of stock</option>
              <option value="Kitchen section is currently closed">Kitchen section is currently closed</option>
              <option value="Table is not occupied / test scan">Table is not occupied / test scan</option>
              <option value="CUSTOM">Custom reason...</option>
            </select>
            <textarea class="ess-input" id="reject-reason-input" rows="3" placeholder="Specify rejection reason for customer..." style="width:100%;">Item temporarily out of stock</textarea>
          </div>
        `,
        footerHtml: `
          <div style="display:flex;justify-content:flex-end;gap:10px;">
            <button type="button" class="ess-btn ess-btn-secondary" id="cancel-reject-btn">Cancel</button>
            <button type="button" class="ess-btn ess-btn-danger" id="confirm-reject-btn">Confirm Rejection</button>
          </div>
        `,
        onMount: (modalRoot, close) => {
          const select = modalRoot.querySelector('#reject-preset-select') as HTMLSelectElement;
          const textarea = modalRoot.querySelector('#reject-reason-input') as HTMLTextAreaElement;
          const confirmBtn = modalRoot.querySelector('#confirm-reject-btn') as HTMLButtonElement;
          const cancelBtn = modalRoot.querySelector('#cancel-reject-btn') as HTMLButtonElement;

          select?.addEventListener('change', () => {
            if (select.value === 'CUSTOM') {
              textarea.value = '';
              textarea.focus();
            } else {
              textarea.value = select.value;
            }
          });

          cancelBtn?.addEventListener('click', () => close());

          confirmBtn?.addEventListener('click', async () => {
            const reason = textarea.value.trim();
            if (reason.length < 3) {
              showToast({
                title: 'Invalid Reason',
                message: 'Please provide a reason with at least 3 characters.',
                type: 'error',
              });
              return;
            }

            confirmBtn.disabled = true;
            confirmBtn.textContent = 'Rejecting...';

            try {
              await apiClient.rejectQrOrder(id, reason);
              showToast({
                title: 'Order Rejected',
                message: `Order #${ref} was rejected. Customer notified.`,
                type: 'info',
              });
              close();
              params.onRefresh();
            } catch (err: any) {
              confirmBtn.disabled = false;
              confirmBtn.textContent = 'Confirm Rejection';
              showToast({
                title: 'Rejection Failed',
                message: err?.message || 'Unable to reject order.',
                type: 'error',
              });
            }
          });
        },
      });
    });
  });
}

// ============================================================================
// 3. PENDING TABLE BILLS SCREEN (Active Operational Bills)
// ============================================================================

function renderPendingBillsScreen(
  viewport: HTMLElement,
  params: {
    pendingTableBills: RestaurantOrder[];
    activeQrBills?: ActiveBillRecord[];
    products: ProductRecord[];
    onNavigateTab: (tab: string) => void;
  }
) {
  const activeQrBills = params.activeQrBills || [];
  const totalBillsCount = params.pendingTableBills.length + activeQrBills.length;

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Active Restaurant Bills</h2>
          <p class="ess-section-desc">Active dine-in bills from table orders and accepted QR orders awaiting settlement</p>
        </div>
        <div style="display:flex;gap:8px;">
          <button type="button" class="ess-btn ess-btn-secondary" id="pb-goto-qr">
            Incoming QR Orders Queue
          </button>
          <button type="button" class="ess-btn ess-btn-primary" id="pb-back-pos">
            Back to POS Screen
          </button>
        </div>
      </div>

      ${
        totalBillsCount === 0
          ? renderEmptyState({
              title: 'No Active Table Bills',
              description: 'All served tables and accepted QR orders have been settled.',
            })
          : `
        <div class="ess-grid-2">
          ${activeQrBills
            .map(
              (bill) => `
            <div class="ess-card" style="border-left: 4px solid var(--ess-primary);">
              <div class="ess-card-header">
                <div>
                  <h3 class="ess-card-title">Table ${escapeHtml(bill.table_number)}</h3>
                  <div class="ess-card-subtitle">Bill <strong>${escapeHtml(bill.bill_number)}</strong> &bull; Source: QR Order &bull; ${formatTimeShort(bill.created_at)}</div>
                </div>
                <span class="ess-badge ess-badge-orange">UNPAID</span>
              </div>
              <div class="ess-stack-sm" style="margin-bottom:14px;">
                ${bill.items
                  .map(
                    (item) => `
                  <div class="ess-kv-row">
                    <span>${item.quantity}&times; ${escapeHtml(item.product_name)}</span>
                    <strong>${formatCurrency(item.unit_price_cents * item.quantity)}</strong>
                  </div>
                `
                  )
                  .join('')}
                <div class="ess-kv-row" style="border-top:1px solid var(--ess-border);padding-top:8px;">
                  <strong>Total Bill Payable</strong>
                  <strong style="color:var(--ess-primary);font-size:16px;">${formatCurrency(bill.total_cents)}</strong>
                </div>
              </div>
              <button type="button" class="ess-btn ess-btn-primary ess-btn-block" data-load-qr-bill-id="${escapeHtml(bill.id)}">
                Load Bill into POS Cart for Settlement
              </button>
            </div>
          `
            )
            .join('')}

          ${params.pendingTableBills
            .map(
              (bill) => `
            <div class="ess-card">
              <div class="ess-card-header">
                <div>
                  <h3 class="ess-card-title">${escapeHtml(bill.tableName)}</h3>
                  <div class="ess-card-subtitle">Order ${escapeHtml(bill.orderNumber)} &bull; Waiter: ${escapeHtml(bill.waiterName)} &bull; ${bill.guests} Guests</div>
                </div>
                <span class="ess-badge ess-badge-orange">${escapeHtml(bill.status)}</span>
              </div>
              <div class="ess-stack-sm" style="margin-bottom:14px;">
                ${bill.items
                  .map(
                    (item) => `
                  <div class="ess-kv-row">
                    <span>${item.quantity}&times; ${escapeHtml(item.productName)}</span>
                    <strong>${formatCurrency(item.unitPriceCents * item.quantity)}</strong>
                  </div>
                `
                  )
                  .join('')}
                <div class="ess-kv-row" style="border-top:1px solid var(--ess-border);padding-top:8px;">
                  <strong>Total Bill Payable</strong>
                  <strong style="color:var(--ess-primary);font-size:16px;">${formatCurrency(bill.totalCents)}</strong>
                </div>
              </div>
              <button type="button" class="ess-btn ess-btn-primary ess-btn-block" data-load-bill-id="${escapeHtml(bill.id)}">
                Load Bill into POS Cart for Settlement
              </button>
            </div>
          `
            )
            .join('')}
        </div>
      `
      }
    </div>
  `;

  viewport.querySelector('#pb-back-pos')?.addEventListener('click', () => params.onNavigateTab('pos-sale'));
  viewport.querySelector('#pb-goto-qr')?.addEventListener('click', () => params.onNavigateTab('incoming-qr-orders'));

  // Load active QR bill into cart
  viewport.querySelectorAll('[data-load-qr-bill-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-load-qr-bill-id');
      const bill = (params.activeQrBills || []).find((b) => b.id === id);
      if (!bill) return;

      cartState.items = [];
      for (const orderItem of bill.items) {
        let matchedProduct: ProductRecord | undefined;
        let matchedVariant: ProductVariantRecord | undefined;
        for (const p of params.products) {
          const v = p.variants.find(
            (va) => va.id === orderItem.variant_id || va.sku === orderItem.sku || p.name === orderItem.product_name
          );
          if (v) {
            matchedProduct = p;
            matchedVariant = v;
            break;
          }
        }
        if (!matchedProduct && params.products.length > 0) {
          matchedProduct = params.products[0];
          matchedVariant = params.products[0].variants[0];
        }

        cartState.items.push({
          variantId: matchedVariant?.id || orderItem.variant_id,
          productId: matchedProduct?.id || 'prod-custom',
          productName: orderItem.product_name,
          variantName: matchedVariant?.variant_name || 'Standard',
          sku: orderItem.sku,
          unitPriceCents: orderItem.unit_price_cents,
          maxAvailableStock: 99,
          quantity: orderItem.quantity,
          discountCents: 0,
          notes: orderItem.notes || '',
        });
      }

      cartState.linkedTableOrderId = bill.id;
      cartState.linkedTableName = `Table ${bill.table_number} (${bill.bill_number})`;

      showToast({
        title: 'Table Bill Loaded into POS',
        message: `Table ${bill.table_number} ready for payment settlement.`,
        type: 'info',
      });
      params.onNavigateTab('pos-sale');
    });
  });

  viewport.querySelectorAll('[data-load-bill-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-load-bill-id');
      const bill = params.pendingTableBills.find((b) => b.id === id);
      if (!bill) return;

      cartState.items = [];
      for (const orderItem of bill.items) {
        // Match against real backend product catalog by SKU or name
        let matchedProduct: ProductRecord | undefined;
        let matchedVariant: ProductVariantRecord | undefined;
        for (const p of params.products) {
          const v = p.variants.find(
            (va) => va.sku === orderItem.sku || p.name === orderItem.productName
          );
          if (v) {
            matchedProduct = p;
            matchedVariant = v;
            break;
          }
        }
        if (!matchedProduct && params.products.length > 0) {
          matchedProduct = params.products[0];
          matchedVariant = params.products[0].variants[0];
        }
        if (matchedProduct && matchedVariant && Number(matchedVariant.total_stock ?? 0) > 0) {
          cartState.items.push({
            variantId: matchedVariant.id,
            productId: matchedProduct.id,
            productName: matchedProduct.name,
            variantName: matchedVariant.variant_name,
            sku: matchedVariant.sku,
            unitPriceCents: matchedVariant.retail_price_cents,
            maxAvailableStock: Number(matchedVariant.total_stock ?? 10),
            quantity: Math.min(orderItem.quantity, Number(matchedVariant.total_stock ?? 10)),
            discountCents: 0,
            notes: orderItem.notes,
          });
        }
      }

      cartState.linkedTableOrderId = bill.id;
      cartState.linkedTableName = `${bill.tableName} (${bill.orderNumber})`;

      showToast({
        title: 'Table Bill Loaded into POS',
        message: `${bill.tableName} ready for payment.`,
        type: 'info',
      });
      params.onNavigateTab('pos-sale');
    });
  });
}

// ============================================================================
// 4. COMPLETED SALES, RECEIPT PREVIEW & AUTHORIZED REVERSAL WORKFLOW
// ============================================================================

function renderCompletedSalesScreen(
  viewport: HTMLElement,
  params: {
    sales: SaleRecord[];
    tenantId: string;
    onRefresh: () => void;
  }
) {
  let searchFilter = '';

  const renderList = () => {
    const filtered = params.sales.filter((s) => {
      if (!searchFilter) return true;
      return (
        s.receipt_number.toLowerCase().includes(searchFilter.toLowerCase()) ||
        s.payment_method.toLowerCase().includes(searchFilter.toLowerCase())
      );
    });

    viewport.innerHTML = `
      <div class="ess-stack-lg">
        <div class="ess-section-header">
          <div>
            <h2 class="ess-section-title">Completed Sales &amp; Immutable Receipt Ledger</h2>
            <p class="ess-section-desc">Completed sales preserve historical price snapshots and cannot be silently edited</p>
          </div>
          <div class="ess-btn-group">
            <input type="search" id="sales-search-input" class="ess-input" placeholder="Search receipt # or method..." value="${escapeHtml(searchFilter)}" style="width:240px;" />
            <button type="button" class="ess-btn ess-btn-secondary" id="sales-export-csv">Export CSV</button>
          </div>
        </div>

        <div class="ess-card">
          ${
            filtered.length === 0
              ? renderEmptyState({
                  title: 'No Matching Sales Found',
                  description: 'Completed POS transactions will appear here with thermal receipt previews.',
                })
              : `
            <div class="ess-table-wrap">
              <table class="ess-table">
                <thead>
                  <tr>
                    <th>Receipt Number</th>
                    <th>Timestamp</th>
                    <th>Cashier</th>
                    <th>Payment</th>
                    <th>Subtotal</th>
                    <th>Discount</th>
                    <th>Total Settled</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  ${filtered
                    .map(
                      (s) => `
                    <tr>
                      <td><strong>${escapeHtml(s.receipt_number)}</strong></td>
                      <td>${formatDateTime(s.created_at)}</td>
                      <td>${escapeHtml(s.cashier_name || 'Cashier')}</td>
                      <td><span class="ess-badge ${s.payment_method === 'MPESA' ? 'ess-badge-orange' : 'ess-badge-neutral'}">${escapeHtml(s.payment_method)}</span></td>
                      <td>${formatCurrency(s.subtotal_cents)}</td>
                      <td>${formatCurrency(s.discount_cents)}</td>
                      <td><strong>${formatCurrency(s.total_cents)}</strong></td>
                      <td><span class="ess-badge ess-badge-success">${escapeHtml(s.status)}</span></td>
                      <td>
                        <div class="ess-btn-group">
                          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-preview-receipt="${escapeHtml(s.id)}">
                            Preview &amp; Print
                          </button>
                          <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" data-request-void="${escapeHtml(s.id)}">
                            Request Reversal
                          </button>
                        </div>
                      </td>
                    </tr>
                  `
                    )
                    .join('')}
                </tbody>
              </table>
            </div>
          `
          }
        </div>
      </div>
    `;

    const input = viewport.querySelector('#sales-search-input') as HTMLInputElement | null;
    input?.addEventListener('input', () => {
      searchFilter = input.value;
      renderList();
      const el = viewport.querySelector('#sales-search-input') as HTMLInputElement | null;
      el?.focus();
    });

    viewport.querySelector('#sales-export-csv')?.addEventListener('click', () => {
      exportRowsToCsv(
        `ess-sales-${new Date().toISOString().slice(0, 10)}.csv`,
        ['Receipt Number', 'Date', 'Cashier', 'Payment Method', 'Subtotal (KES)', 'Discount (KES)', 'Total (KES)', 'Status'],
        filtered.map((s) => [
          s.receipt_number,
          s.created_at,
          s.cashier_name || 'Cashier',
          s.payment_method,
          (s.subtotal_cents / 100).toFixed(2),
          (s.discount_cents / 100).toFixed(2),
          (s.total_cents / 100).toFixed(2),
          s.status,
        ])
      );
    });

    viewport.querySelectorAll('[data-preview-receipt]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const sale = params.sales.find((s) => s.id === btn.getAttribute('data-preview-receipt'));
        if (sale) openReceiptPreviewModal(sale);
      });
    });

    viewport.querySelectorAll('[data-request-void]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const sale = params.sales.find((s) => s.id === btn.getAttribute('data-request-void'));
        if (!sale) return;

        openModal({
          title: `Request Authorized Reversal — ${sale.receipt_number}`,
          subtitle: 'Completed sales cannot be silently edited. Submit a formal reversal request to the Manager.',
          width: 'sm',
          bodyHtml: `
            <div class="ess-form">
              <div class="ess-kv-row"><span>Receipt Amount</span><strong>${formatCurrency(sale.total_cents)}</strong></div>
              <div class="ess-form-group">
                <label class="ess-label">Reversal / Refund Type *</label>
                <select id="void-req-type" class="ess-select">
                  <option value="VOID_SALE">Full Receipt Void</option>
                  <option value="REFUND">Customer Refund &amp; Restock</option>
                </select>
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Mandatory Audit Reason *</label>
                <textarea id="void-req-reason" class="ess-textarea" rows="3" placeholder="Explain why this transaction requires manager reversal..."></textarea>
              </div>
            </div>
          `,
          footerHtml: `
            <button type="button" class="ess-btn ess-btn-secondary" id="void-cancel">Cancel</button>
            <button type="button" class="ess-btn ess-btn-danger" id="void-submit">Submit to Manager Approval Queue</button>
          `,
          onMount: (root, close) => {
            root.querySelector('#void-cancel')?.addEventListener('click', close);
            root.querySelector('#void-submit')?.addEventListener('click', () => {
              const type = (root.querySelector('#void-req-type') as HTMLSelectElement).value as
                | 'VOID_SALE'
                | 'REFUND';
              const reason = (root.querySelector('#void-req-reason') as HTMLTextAreaElement).value.trim();
              if (reason.length < 5) {
                showToast({
                  title: 'Audit Reason Required',
                  message: 'Please provide a clear explanation (at least 5 characters).',
                  type: 'warning',
                });
                return;
              }
              const user = apiClient.getUser();
              restaurantMockAdapter.requestSaleReversalOrDiscount(params.tenantId, {
                type,
                requestedBy: `${user?.firstName || 'Cashier'} ${user?.lastName || ''}`.trim(),
                requesterRole: 'CASHIER',
                referenceCode: sale.receipt_number,
                amountCents: sale.total_cents,
                reason,
              });
              close();
              showToast({
                title: 'Reversal Request Logged',
                message: `Sent ${sale.receipt_number} to Manager Approval Queue.`,
                type: 'info',
              });
            });
          },
        });
      });
    });
  };

  renderList();
}

export function openReceiptPreviewModal(sale: SaleRecord) {
  const tenant = apiClient.getTenant();
  const items = sale.items || [];

  openModal({
    title: `Customer Receipt Preview — ${sale.receipt_number}`,
    subtitle: 'Formatted for 80mm Thermal POS Printer',
    width: 'sm',
    bodyHtml: `
      <div class="ess-thermal-receipt" id="ess-printable-receipt">
        <div class="ess-receipt-center">
          <div class="ess-receipt-brand">${escapeHtml(tenant?.name || 'SAVANNAH GRILL & LOUNGE')}</div>
          <div>Easy Smart System (ESS POS)</div>
          <div>Receipt: <strong>${escapeHtml(sale.receipt_number)}</strong></div>
          <div>Date: ${formatDateTime(sale.created_at)}</div>
        </div>
        <hr class="ess-receipt-hr" />
        <div class="ess-receipt-items">
          ${
            items.length === 0
              ? `<div class="ess-kv-row"><span>POS Consolidated Sale</span><strong>${formatCurrency(sale.total_cents)}</strong></div>`
              : items
                  .map(
                    (item) => `
                <div class="ess-receipt-item-row">
                  <div>
                    <strong>${escapeHtml(item.product_name_snapshot || item.sku_snapshot || 'Menu Item')}</strong>
                    <div style="font-size:11px;color:var(--ess-text-muted);">${item.quantity} &times; ${formatCurrency(item.unit_price_cents)}</div>
                  </div>
                  <strong>${formatCurrency(item.total_cents)}</strong>
                </div>
              `
                  )
                  .join('')
          }
        </div>
        <hr class="ess-receipt-hr" />
        <div class="ess-kv-row"><span>Subtotal</span><span>${formatCurrency(sale.subtotal_cents)}</span></div>
        <div class="ess-kv-row"><span>Discount</span><span>&minus; ${formatCurrency(sale.discount_cents)}</span></div>
        <div class="ess-kv-row" style="font-size:15px;"><strong>TOTAL PAID</strong><strong>${formatCurrency(sale.total_cents)}</strong></div>
        <div class="ess-kv-row"><span>Payment Method</span><strong>${escapeHtml(sale.payment_method)}</strong></div>
        <hr class="ess-receipt-hr" />
        <div class="ess-receipt-center" style="font-size:11px;color:var(--ess-text-muted);">
          Thank you for dining with us!<br/>
          Powered by ESS POS &bull; Work Smarter
        </div>
      </div>
    `,
    footerHtml: `
      <button type="button" class="ess-btn ess-btn-secondary" id="rcp-close-btn">Close</button>
      <button type="button" class="ess-btn ess-btn-primary" id="rcp-print-btn">Print Thermal Receipt</button>
    `,
    onMount: (root, close) => {
      root.querySelector('#rcp-close-btn')?.addEventListener('click', close);
      root.querySelector('#rcp-print-btn')?.addEventListener('click', () => {
        window.print();
      });
    },
  });
}

// ============================================================================
// 5. SHIFT OPENING, CLOSING & CASH RECONCILIATION
// ============================================================================

function renderShiftDrawerScreen(
  viewport: HTMLElement,
  params: {
    activeShift: CashierShiftRecord | null;
    primaryBranch?: BranchRecord;
    sales: SaleRecord[];
    onRefresh: () => void;
  }
) {
  const shift = params.activeShift;
  const expectedCashCents =
    shift?.metrics?.current_expected_cash_cents ?? shift?.expected_cash_cents ?? 0;

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Shift Management &amp; Cash Drawer Reconciliation</h2>
          <p class="ess-section-desc">Open your cashier shift with a verified float and reconcile physical drawer cash at close</p>
        </div>
      </div>

      ${
        !shift
          ? `
        <div class="ess-card" style="max-width:540px;">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Open New Cashier Shift</h3>
            <span class="ess-badge ess-badge-warning">Drawer Closed</span>
          </div>
          <form id="open-shift-form" class="ess-form">
            <div class="ess-form-group">
              <label class="ess-label">Branch Location</label>
              <input type="text" class="ess-input" value="${escapeHtml(params.primaryBranch?.name || 'Main Branch')}" disabled />
            </div>
            <div class="ess-form-group">
              <label class="ess-label" for="shift-float-kes">Opening Cash Float in Drawer (KES) *</label>
              <input type="number" id="shift-float-kes" class="ess-input ess-input-lg" min="0" step="100" value="5000" required />
            </div>
            <button type="submit" class="ess-btn ess-btn-primary ess-btn-lg" id="open-shift-submit">
              Open Cashier Shift
            </button>
          </form>
        </div>
      `
          : `
        <div class="ess-grid-2">
          <div class="ess-card">
            <div class="ess-card-header">
              <h3 class="ess-card-title">Active Shift Metrics</h3>
              <span class="ess-badge ess-badge-success">OPEN SHIFT</span>
            </div>
            <div class="ess-stack-sm">
              <div class="ess-kv-row">
                <span>Shift Opened At</span>
                <strong>${formatDateTime(shift.opening_time)}</strong>
              </div>
              <div class="ess-kv-row">
                <span>Opening Drawer Float</span>
                <strong>${formatCurrency(shift.opening_float_cents)}</strong>
              </div>
              <div class="ess-kv-row">
                <span>Completed Shift Transactions</span>
                <strong>${shift.metrics?.transaction_count ?? 0}</strong>
              </div>
              <div class="ess-kv-row">
                <span>Shift Cash Sales</span>
                <strong>${formatCurrency(shift.metrics?.cash_sales_cents ?? 0)}</strong>
              </div>
              <div class="ess-kv-row">
                <span>Shift M-Pesa Sales</span>
                <strong>${formatCurrency(shift.metrics?.mpesa_sales_cents ?? 0)}</strong>
              </div>
              <div class="ess-kv-row" style="border-top:1px solid var(--ess-border);padding-top:10px;">
                <strong>Expected Physical Cash in Drawer</strong>
                <strong style="color:var(--ess-primary);font-size:16px;">${formatCurrency(expectedCashCents)}</strong>
              </div>
            </div>
          </div>

          <div class="ess-card">
            <div class="ess-card-header">
              <h3 class="ess-card-title">Close Shift &amp; Reconcile Cash Drawer</h3>
              <span class="ess-badge ess-badge-neutral">Audited</span>
            </div>
            <form id="close-shift-form" class="ess-form">
              <div class="ess-form-group">
                <label class="ess-label" for="actual-cash-kes">Counted Physical Cash in Drawer (KES) *</label>
                <input
                  type="number"
                  id="actual-cash-kes"
                  class="ess-input ess-input-lg"
                  min="0"
                  step="10"
                  value="${expectedCashCents / 100}"
                  required
                />
              </div>
              <div class="ess-form-group">
                <label class="ess-label" for="close-shift-notes">Reconciliation Notes (Explain any variance)</label>
                <textarea id="close-shift-notes" class="ess-textarea" rows="2" placeholder="e.g. Physical count verified against Z-report"></textarea>
              </div>
              <button type="submit" class="ess-btn ess-btn-primary ess-btn-block" id="close-shift-submit">
                Close Shift &amp; Generate Reconciliation Report
              </button>
            </form>
          </div>
        </div>
      `
      }
    </div>
  `;

  const openForm = viewport.querySelector('#open-shift-form') as HTMLFormElement | null;
  openForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!params.primaryBranch) return;
    const floatKes = Number((viewport.querySelector('#shift-float-kes') as HTMLInputElement).value || 0);
    const btn = viewport.querySelector('#open-shift-submit') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Opening Shift...';

    try {
      await apiClient.openShift({
        branchId: params.primaryBranch.id,
        openingFloatCents: Math.round(floatKes * 100),
      });
      showToast({
        title: 'Cashier Shift Opened',
        message: `Starting float: ${formatCurrency(Math.round(floatKes * 100))}`,
        type: 'success',
      });
      params.onRefresh();
    } catch (err: any) {
      showToast({
        title: 'Could Not Open Shift',
        message: err?.message || 'Error opening shift',
        type: 'danger',
      });
      btn.disabled = false;
      btn.textContent = 'Open Cashier Shift';
    }
  });

  const closeForm = viewport.querySelector('#close-shift-form') as HTMLFormElement | null;
  closeForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!shift) return;
    const actualKes = Number((viewport.querySelector('#actual-cash-kes') as HTMLInputElement).value || 0);
    const notes = (viewport.querySelector('#close-shift-notes') as HTMLTextAreaElement).value.trim();
    const btn = viewport.querySelector('#close-shift-submit') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Reconciling Drawer...';

    try {
      const res = await apiClient.closeShift(shift.id, {
        actualCashCents: Math.round(actualKes * 100),
        closingNotes: notes || undefined,
      });

      openModal({
        title: 'Shift Closed — Cash Reconciliation Summary',
        width: 'sm',
        bodyHtml: `
          <div class="ess-stack-sm">
            <div class="ess-kv-row"><span>Opening Float</span><strong>${formatCurrency(res.summary.opening_float_cents)}</strong></div>
            <div class="ess-kv-row"><span>Cash Sales</span><strong>${formatCurrency(res.summary.cash_sales_cents)}</strong></div>
            <div class="ess-kv-row"><span>Expected Drawer Cash</span><strong>${formatCurrency(res.summary.expected_cash_cents)}</strong></div>
            <div class="ess-kv-row"><span>Counted Actual Cash</span><strong>${formatCurrency(res.summary.actual_cash_cents)}</strong></div>
            <div class="ess-kv-row" style="border-top:1px solid var(--ess-border);padding-top:8px;">
              <strong>Variance</strong>
              <strong style="color:${res.summary.is_balanced ? 'var(--ess-success-text)' : 'var(--ess-danger-text)'};">
                ${formatCurrency(res.summary.variance_cents)} (${res.summary.is_balanced ? 'BALANCED' : 'VARIANCE DETECTED'})
              </strong>
            </div>
          </div>
        `,
        onMount: () => {
          params.onRefresh();
        },
      });
    } catch (err: any) {
      showToast({
        title: 'Shift Reconciliation Failed',
        message: err?.message || 'Unable to close shift',
        type: 'danger',
      });
      btn.disabled = false;
      btn.textContent = 'Close Shift & Generate Reconciliation Report';
    }
  });
}

// ============================================================================
// 7. PRIVATE CASHIER PROFILE & PIN POWER
// ============================================================================

function renderCashierProfileScreen(
  viewport: HTMLElement,
  params: {
    sales: SaleRecord[];
    activeShift: CashierShiftRecord | null;
    primaryBranch?: BranchRecord;
    onRefresh: () => void;
    onNavigateTab: (tab: string) => void;
  }
) {
  const user = apiClient.getUser();
  const tenant = apiClient.getTenant();
  const tenantId = apiClient.getEffectiveTenantId();
  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Active Cashier';
  const completedSales = params.sales.filter((s) => s.status === 'COMPLETED');
  const totalSalesCents = completedSales.reduce((sum, s) => sum + s.total_cents, 0);
  const cashSalesCents = completedSales.filter((s) => s.payment_method === 'CASH').reduce((sum, s) => sum + s.total_cents, 0);
  const mpesaSalesCents = completedSales.filter((s) => s.payment_method === 'MPESA').reduce((sum, s) => sum + s.total_cents, 0);

  viewport.innerHTML = `
    <div class="ess-stack-md">
      <div class="ess-card">
        <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;">
          <div style="display:flex;align-items:center;gap:16px;">
            <div style="width:68px;height:68px;border-radius:50%;background:var(--ess-primary,#0284c7);color:#fff;display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:700;">
              ${escapeHtml((user?.firstName?.[0] || 'C') + (user?.lastName?.[0] || ''))}
            </div>
            <div>
              <div style="display:flex;align-items:center;gap:8px;">
                <h2 style="margin:0;font-size:22px;font-weight:800;color:var(--ess-text-primary);">${escapeHtml(fullName)}</h2>
                <span class="ess-role-pill" style="font-size:11px;">CASHIER</span>
              </div>
              <div style="font-size:13px;color:var(--ess-text-secondary);margin-top:2px;">
                ${escapeHtml(user?.email || '')} &bull; ${escapeHtml(params.primaryBranch?.name || tenant?.name || 'Restaurant POS')}
              </div>
              <div style="font-size:12px;color:var(--ess-success-text);margin-top:4px;font-weight:600;">
                ● Private Session Protected with 4-Digit Fast Login PIN
              </div>
            </div>
          </div>

          <div style="display:flex;gap:10px;">
            <button type="button" class="ess-btn ess-btn-secondary" id="cp-lock-btn" style="display:flex;align-items:center;gap:6px;font-weight:700;">
              🔒 Lock Station Now
            </button>
            <button type="button" class="ess-btn ess-btn-primary" id="cp-switch-btn" style="display:flex;align-items:center;gap:6px;font-weight:700;">
              👤 Switch Staff
            </button>
          </div>
        </div>
      </div>

      <!-- Live Shift Stats Grid -->
      <div class="ess-grid-4">
        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Drawer Shift Status</div>
          <div class="ess-metric-value" style="font-size:18px;color:${params.activeShift ? 'var(--ess-success-text)' : 'var(--ess-warning-text)'};">
            ${params.activeShift ? '● SHIFT ACTIVE' : '○ SHIFT CLOSED'}
          </div>
          <div class="ess-metric-sub">
            ${params.activeShift ? `Opened at ${formatTimeShort(params.activeShift.opened_at)}` : 'Open drawer to start billing'}
          </div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Completed Sales</div>
          <div class="ess-metric-value">${completedSales.length}</div>
          <div class="ess-metric-sub">Processed by you today</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Total Ring-Up</div>
          <div class="ess-metric-value" style="color:var(--ess-primary);">${formatCurrency(totalSalesCents)}</div>
          <div class="ess-metric-sub">Gross settled revenue</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Payment Split</div>
          <div class="ess-metric-value" style="font-size:16px;">
            Cash: ${formatCurrency(cashSalesCents)}
          </div>
          <div class="ess-metric-sub">M-Pesa: ${formatCurrency(mpesaSalesCents)}</div>
        </div>
      </div>

      <!-- PIN Security & Management Card -->
      <div class="ess-grid-2">
        <div class="ess-card">
          <h3 style="margin:0 0 8px 0;font-size:16px;font-weight:700;color:var(--ess-text-primary);display:flex;align-items:center;gap:6px;">
            🔑 My Secret Fast-Login PIN
          </h3>
          <p style="font-size:13px;color:var(--ess-text-secondary);margin:0 0 16px 0;">
            Your personal 4 to 6-digit numeric PIN protects your cash drawer and sales account from other employees. Keep it private.
          </p>

          <form id="cp-pin-form" style="display:flex;flex-direction:column;gap:12px;">
            <div class="ess-form-group">
              <label class="ess-label">New 4 to 6-Digit PIN *</label>
              <input type="password" id="cp-new-pin" class="ess-input" maxlength="6" inputmode="numeric" placeholder="e.g. 5821" style="font-size:18px;letter-spacing:6px;width:100%;max-width:260px;" required />
            </div>

            <div class="ess-form-group">
              <label class="ess-label">Confirm New PIN *</label>
              <input type="password" id="cp-confirm-pin" class="ess-input" maxlength="6" inputmode="numeric" placeholder="Re-enter PIN" style="font-size:18px;letter-spacing:6px;width:100%;max-width:260px;" required />
            </div>

            <div id="cp-pin-error" style="color:var(--ess-danger,#ef4444);font-size:12px;font-weight:600;min-height:16px;"></div>

            <div>
              <button type="submit" class="ess-btn ess-btn-primary" id="cp-save-pin-btn" style="font-weight:700;">
                Save My Secret PIN
              </button>
            </div>
          </form>
        </div>

        <div class="ess-card">
          <h3 style="margin:0 0 8px 0;font-size:16px;font-weight:700;color:var(--ess-text-primary);display:flex;align-items:center;gap:6px;">
            🛡️ Account Privacy &amp; Station Security
          </h3>
          <p style="font-size:13px;color:var(--ess-text-secondary);margin:0 0 14px 0;">
            ESS POS enforces server-level role and user isolation:
          </p>
          <ul style="margin:0;padding-left:18px;font-size:13px;color:var(--ess-text-primary);line-height:1.6;">
            <li><strong>Strict Role Boundary:</strong> Waiters, stock keepers, and other staff cannot access your cashier drawers, POS checkout, or transaction history.</li>
            <li><strong>Zero Shared Sessions:</strong> When stepping away from the register, always tap <em>Lock Station Now</em> to prevent unauthorized checkout.</li>
            <li><strong>PIN Encryption:</strong> Your PIN is verified by the backend server for all terminal logins.</li>
          </ul>

          <div style="margin-top:20px;padding:12px;background:var(--ess-bg-subtle,#f8fafc);border-radius:6px;border:1px solid var(--ess-border);">
            <div style="font-size:12px;font-weight:700;color:var(--ess-text-primary);">Quick Actions:</div>
            <div style="display:flex;gap:8px;margin-top:8px;">
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="cp-go-pos">New Sale POS</button>
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="cp-go-drawer">Shift Drawer</button>
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="cp-go-sales">Completed Sales</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Bind actions
  viewport.querySelector('#cp-lock-btn')?.addEventListener('click', () => {
    openLockStationModal(user, tenantId);
  });

  viewport.querySelector('#cp-switch-btn')?.addEventListener('click', () => {
    openFastSwitchStaffModal(tenantId);
  });

  viewport.querySelector('#cp-go-pos')?.addEventListener('click', () => params.onNavigateTab('pos-sale'));
  viewport.querySelector('#cp-go-drawer')?.addEventListener('click', () => params.onNavigateTab('shift-drawer'));
  viewport.querySelector('#cp-go-sales')?.addEventListener('click', () => params.onNavigateTab('completed-sales'));

  // Bind PIN change form
  const pinForm = viewport.querySelector('#cp-pin-form') as HTMLFormElement;
  pinForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const newPin = (viewport.querySelector('#cp-new-pin') as HTMLInputElement)?.value.trim();
    const confirmPin = (viewport.querySelector('#cp-confirm-pin') as HTMLInputElement)?.value.trim();
    const errDiv = viewport.querySelector('#cp-pin-error') as HTMLElement;
    const saveBtn = viewport.querySelector('#cp-save-pin-btn') as HTMLButtonElement;

    if (!newPin || newPin.length < 4 || newPin.length > 6 || !/^\d+$/.test(newPin)) {
      if (errDiv) errDiv.textContent = 'PIN must be 4 to 6 numeric digits';
      return;
    }
    if (newPin !== confirmPin) {
      if (errDiv) errDiv.textContent = 'PINs do not match. Please verify.';
      return;
    }

    if (errDiv) errDiv.textContent = '';
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving PIN...';
    }

    try {
      await apiClient.updateStaffPin(newPin);
      showToast({
        title: 'Secret PIN Updated',
        message: 'Your personal fast-login PIN is now active for this station.',
        type: 'success',
      });
      (viewport.querySelector('#cp-new-pin') as HTMLInputElement).value = '';
      (viewport.querySelector('#cp-confirm-pin') as HTMLInputElement).value = '';
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save My Secret PIN';
      }
    } catch (err: any) {
      if (errDiv) errDiv.textContent = err?.message || 'Failed to update PIN.';
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save My Secret PIN';
      }
    }
  });
}


